import { useEffect, useState, type FocusEvent } from "react";
import { useLocation } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { API_BASE_URL } from "../lib/api";
import { fetchPlayerBranding } from "../lib/playerBranding";
import { absoluteAssetUrl } from "../lib/rulesPresentation";
import { playerBannerList, safeSponsorLink } from "../lib/tournamentExtras";
import ImageLightbox from "./ImageLightbox";

interface TournamentSponsorSlotsProps {
  className?: string;
  compact?: boolean;
  /** «home»: inicio del torneo (Eduardo 2026-10-05, maqueta aprobada):
   * póster y logo del club lado a lado (3:4, se tocan para verlos en
   * grande) y debajo el patrocinador. Sin la prop, la franja de siempre. */
  layout?: "strip" | "home";
}

interface Branding {
  name: string;
  logo_url: string | null;
  organizer_logo_url?: string | null;
  player_banner_url: string | null;
  player_banners?: Array<{ image_url: string; name: string | null; link: string | null }>;
}

/** Un cuadro 3:4 del inicio del torneo: con imagen se toca para verla en
 * grande; sin imagen, el espacio guía con su texto. */
function MediaTile({
  src,
  alt,
  openLabel,
  emptyEyebrow,
  emptyTitle,
  emptyHint,
  modifier,
}: {
  src: string | null;
  alt: string;
  openLabel: string;
  emptyEyebrow: string;
  emptyTitle: string;
  emptyHint?: string;
  modifier: string;
}) {
  const [open, setOpen] = useState(false);
  if (!src) {
    return (
      <div className={`player-media-tile player-media-tile--${modifier} is-empty`}>
        <span>{emptyEyebrow}</span>
        <strong>{emptyTitle}</strong>
        {emptyHint ? <small>{emptyHint}</small> : null}
      </div>
    );
  }
  return (
    <>
      <button
        type="button"
        className={`player-media-tile player-media-tile--${modifier}`}
        aria-label={openLabel}
        onClick={() => setOpen(true)}
      >
        <img src={src} alt="" />
      </button>
      {open ? <ImageLightbox src={src} alt={alt} title={alt} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

const TOURNAMENT_PATH_RE = /\/player\/tournaments\/(\d+)(?:\/|$)/;
// Varios patrocinadores (Eduardo 2026-10-01): cada uno se queda unos
// segundos y pasa al siguiente.
const ROTATION_MS = 6000;

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
}

/** Slots de imagen de las vistas del jugador (Eduardo 2026-08-21): cuando el
 * torneo tiene póster/imagen y banner de patrocinador cargados en Datos, se
 * muestran; si no, quedan los espacios guía. El componente se abastece solo
 * (lee el torneo de la URL) para no enhebrar datos por las 8 páginas que
 * lo usan. Con varios patrocinadores van rotando (con movimiento reducido
 * no rotan solos: los puntos dejan elegir cuál ver). */
export default function TournamentSponsorSlots({
  className = "",
  compact = false,
  layout = "strip",
}: TournamentSponsorSlotsProps) {
  const { token } = useAuth();
  const location = useLocation();
  const match = location.pathname.match(TOURNAMENT_PATH_RE);
  const tournamentId = match ? Number(match[1]) : null;
  const [branding, setBranding] = useState<Branding | null>(null);
  const [current, setCurrent] = useState(0);
  // Rotación accesible: se detiene con el mouse encima o el foco adentro
  // (el link enfocado no cambia de patrocinador bajo el dedo), con «Pausar»
  // y para siempre en cuanto la persona elige uno con los puntos.
  const [hovered, setHovered] = useState(false);
  const [focusedInside, setFocusedInside] = useState(false);
  const [stopped, setStopped] = useState(false);

  useEffect(() => {
    if (!token || !tournamentId) {
      setBranding(null);
      return;
    }
    let cancelled = false;
    // La barra de abajo pide lo mismo al mismo tiempo: una sola petición.
    fetchPlayerBranding(token, tournamentId)
      .then((response) => {
        if (!cancelled) setBranding(response.data);
      })
      .catch(() => {
        if (!cancelled) setBranding(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token, tournamentId]);

  const logoSrc = absoluteAssetUrl(branding?.logo_url, API_BASE_URL, window.location.origin);
  const banners = playerBannerList(branding).flatMap((banner) => {
    const src = absoluteAssetUrl(banner.image_url, API_BASE_URL, window.location.origin);
    return src ? [{ src, name: banner.name, link: safeSponsorLink(banner.link) }] : [];
  });
  const bannerCount = banners.length;

  useEffect(() => {
    setCurrent(0);
  }, [bannerCount]);

  const reducedMotion = prefersReducedMotion();
  const rotating = bannerCount > 1 && !reducedMotion && !stopped && !hovered && !focusedInside;
  useEffect(() => {
    if (!rotating) return;
    const timer = window.setInterval(() => setCurrent((index) => (index + 1) % bannerCount), ROTATION_MS);
    return () => window.clearInterval(timer);
  }, [bannerCount, rotating]);

  function handleBlur(event: FocusEvent<HTMLElement>) {
    const next = event.relatedTarget as Node | null;
    if (!next || !event.currentTarget.contains(next)) setFocusedInside(false);
  }

  const banner = bannerCount > 0 ? banners[current % bannerCount] : null;
  const bannerImage = banner ? (
    <img
      src={banner.src}
      alt={banner.name ? `Patrocinador: ${banner.name}` : "Patrocinador del torneo"}
      style={{ maxWidth: "100%", height: "auto", display: "block" }}
    />
  ) : null;

  const sponsorSlot = (
    <article
      className={`tournament-sponsor-slot tournament-sponsor-slot--sponsor${banner ? " is-filled" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocusedInside(true)}
      onBlur={handleBlur}
    >
      {banner ? (
        <>
          {banner.link ? (
            <a href={banner.link} target="_blank" rel="noopener noreferrer sponsored">
              {bannerImage}
            </a>
          ) : (
            bannerImage
          )}
          {bannerCount > 1 ? (
            <div className="tournament-sponsor-slot__dots" role="group" aria-label="Patrocinadores del torneo">
              {banners.map((item, index) => (
                <button
                  key={`${item.src}-${index}`}
                  type="button"
                  className={`tournament-sponsor-slot__dot${index === current % bannerCount ? " is-active" : ""}`}
                  aria-label={`Ver patrocinador ${index + 1}${item.name ? `: ${item.name}` : ""}`}
                  aria-pressed={index === current % bannerCount}
                  onClick={() => {
                    setCurrent(index);
                    setStopped(true);
                  }}
                />
              ))}
              {!reducedMotion ? (
                <button
                  type="button"
                  className="tournament-sponsor-slot__pause"
                  aria-label={stopped ? "Seguir cambiando de patrocinador" : "Dejar de cambiar de patrocinador"}
                  onClick={() => setStopped((value) => !value)}
                >
                  {stopped ? "Seguir" : "Pausar"}
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <span>Espacio para banner / patrocinador</span>
          <strong>Patrocinador del torneo</strong>
          <small>Formato recomendado: JPG/WEBP vertical · 1080 × 1350 px (misma proporción que el póster)</small>
        </>
      )}
    </article>
  );

  if (layout === "home") {
    const organizerLogoSrc = absoluteAssetUrl(branding?.organizer_logo_url, API_BASE_URL, window.location.origin);
    const tournamentName = branding?.name ?? "el torneo";
    return (
      <section
        className={`player-media${className ? ` ${className}` : ""}`}
        aria-label="Póster, club y patrocinadores"
      >
        <div className="player-media__tiles">
          <MediaTile
            modifier="poster"
            src={logoSrc}
            alt={`Póster de ${tournamentName}`}
            openLabel="Ver el póster en grande"
            emptyEyebrow="Espacio para el póster del torneo"
            emptyTitle="Póster del torneo"
            emptyHint="Formato recomendado: JPG/PNG/WEBP vertical · 1080 × 1350 px"
          />
          <MediaTile
            modifier="club"
            src={organizerLogoSrc}
            alt={`Logo del club de ${tournamentName}`}
            openLabel="Ver el logo del club en grande"
            emptyEyebrow="Espacio para el logo del club"
            emptyTitle="Logo del club"
          />
        </div>
        {sponsorSlot}
      </section>
    );
  }

  return (
    <section
      className={`tournament-sponsor-slots${compact ? " tournament-sponsor-slots--compact" : ""}${className ? ` ${className}` : ""}`}
      aria-label="Espacios de imagen del torneo"
    >
      <article className={`tournament-sponsor-slot tournament-sponsor-slot--logo${logoSrc ? " is-filled" : ""}`}>
        {logoSrc ? (
          <img src={logoSrc} alt={branding?.name ?? "Imagen del torneo"} style={{ maxWidth: "100%", height: "auto", display: "block" }} />
        ) : (
          <>
            <span>Espacio para el póster del torneo</span>
            <strong>Póster del torneo</strong>
            <small>Formato recomendado: JPG/PNG/WEBP vertical · 1080 × 1350 px</small>
          </>
        )}
      </article>
      {sponsorSlot}
    </section>
  );
}
